<template>
  <div class="booking-sea-eq-data-grid">
    <cargo-ag-grid
      config-module="130004"
      :grid-options="gridOptions"
      :column-defs="finalColumnDefs"
      :row-data="finalRowData"
      :auto-fit-content="false"
      style="width:100%;height:200px;margin-bottom: 5px"
      @gridReady="params=>{gridApi=params.api}"
    />
    <div style="width:100%;display: flex">
      <div v-show="isEdit" style="text-align: left;margin-bottom: 5px;width: 50%">
        <span :title="containerDesc">{{ containerDesc.length > 20 ? containerDesc.substring(0, 40) + '...' : containerDesc }}</span>
      </div>
      <div style="text-align: right;margin-bottom: 5px">
        <el-button v-show="isEdit" no="Add" type="primary" @click="add">{{ $t('SeaExport.Add') }}</el-button>&nbsp;
        <el-button v-show="isEdit" no="Delete" type="danger" @click="remove">{{ $t('SeaExport.Delete') }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>

import { EQ_SIZES } from '@/class/cargo/model/parameters/EQSize'
import { SeaEQ } from '@/class/model/booking/SeaEQ'
import { Container } from '@/class/model/booking/Container'
import { ContainerUsage } from '@/class/model/booking/ContainerUsage'
import { getSpecificBasicData } from '@/utils/basicDataCache'
import { basicCacheKeys } from '@/utils/cacheConfig'
import { EntityDefaultValueUtil } from '@/class/cargo/model/utils/EntityDefaultValueUtil'
import CargoAgGridCommonInputEditor from '@/components/Cargo/Common/CargoAgGridCommonInputEditor'
import { getUuid } from '@/utils'
import * as EQSize from '@/class/cargo/model/parameters/EQSize'
import ContainerNoHelper from '@/utils/containerNoHelper'
import { DataPushCompareUtils } from '@/class/cargo/model/utils/DataPushCompareUtils'
import { ContainerUtil } from '@/class/cargo/model/utils/ContainerUtil'

export default {
  name: 'BookingIntegratedDataGrid',
  components: {},
  props: {
    job: { type: Object, required: true },
    value: { type: Array, default: () => [] },
    isEdit: { type: Boolean, required: true }
  },
  data() {
    return {
      eqSizes: EQ_SIZES,
      eqTypes: [],
      defaultSeaEQ: {},
      gridOptions: {
        rowSelection: 'single',
        suppressCellSelection: true,
        suppressContextMenu: true,
        singleClickEdit: true,
        rowHeight: 32,
        unSortIcon: true,
        onCellFocused: (event) => {
          this.onUpCellFocused(event)
        },
        onRowClicked: this.rowClicked,
        frameworkComponents: {
          CargoAgGridCommonInputEditor
        },
        context: {
          componentParent: this
        }
      },
      rowData: [],
      gridApi: null,
      selectedData: null // 当前选中的记录
    }
  },
  computed: {
    defaultEqSize() {
      return this.defaultSeaEQ.eqSize ? this.defaultSeaEQ.eqSize : '20'
    },
    defaultEqType() {
      return this.defaultSeaEQ.eqType ? this.defaultSeaEQ.eqType : 'GP'
    },
    finalRowData() {
      return this.rowData.filter(p => !p.deleted)
    },
    fitSize() {
      return 90
    },
    containerDesc() {
      return ContainerUtil.getContainerDesc(this.job)
    },
    finalColumnDefs() {
      return [
        {
          headerName: this.$t('SeaExport.SequenceNumber'), colId: 'serialNo', field: 'serialNo', initialWidth: 180,
          headerClass: 'ag-header-cell-right',
          cellStyle: {
            textAlign: 'right'
          },
          valueFormatter: (params) => {
            return params.value
          },
          editable: (params) => false,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: {
            isNumber: true,
            inputProps: {
              controls: false,
              precision: 1
            }
          }
        },
        {
          headerName: this.$t('SeaExport.ContainerSize'),
          field: 'eqSize',
          width: this.fitSize,
          suppressMenu: true,
          lockVisible: true,
          cellRendererFramework: 'agGridColumnEdit',
          custom: {
            isEdit: this.isEdit,
            tag: 'cargo-cell-select',
            optionField: {
              labelFunc: item => {
                return item.name
              },
              value: 'code',
              filterable: false
            },
            attrs: {
              cellSelectAttrs: { fullOptions: [...this.eqSizes] }
            },
            valueChanged: p => {
              this.onCellChange(p)
            }
          }
        },
        {
          headerName: this.$t('SeaExport.ContainerType'),
          field: 'eqType',
          width: this.fitSize,
          suppressMenu: true,
          cellRendererFramework: 'agGridColumnEdit',
          custom: {
            isEdit: this.isEdit,
            tag: 'cargo-cell-select',
            optionField: {
              labelFunc: item => {
                return item.code
              },
              value: 'code',
              filterable: true
            },
            attrs: {
              cellSelectAttrs: { fullOptions: [...this.eqTypes] },
              style: 'width:70px'
            },
            valueChanged: p => {
              this.onCellChange(p)
            }
          }
        },
        {
          headerName: this.$t('SeaExport.ContainerNo'), colId: 'containerNo', field: 'containerNo', initialWidth: 180,
          headerClass: 'ag-header-cell-left',
          cellStyle: (params) => {
            return this.containerNbrStyleFunction(params)
          },
          editable: (params) => false, // 暂不开放编辑
          cellEditor: 'CargoAgGridCommonInputEditor',
          onCellValueChanged: p => this.onCellChange(p)
        }
      ]
    }
  },
  watch: {
    value(newVal) {
      this.rowData = newVal
    }
  },
  async created() {
    this.eqTypes = await getSpecificBasicData(basicCacheKeys.basic_data_EQType)
    this.getDefaultSeaEQ()
    this.rowData = this.value
  },
  methods: {
    async getDefaultSeaEQ() {
      const entityDefValUtil = EntityDefaultValueUtil.getInstance()
      if (!entityDefValUtil.defaultValues) {
        await entityDefValUtil.initEntityDefaultValue()
      }
      this.defaultSeaEQ = entityDefValUtil.seaEQ
    },
    add() {
      if (!this.job.seaEQ) {
        this.job.seaEQ = []
      }
      if (!this.job.containers) {
        this.job.containers = []
      }
      // 取目前最大的序号
      let serialNo = 1
      for (const d of this.job.containerUsages) {
        if (serialNo <= d.serialNo) {
          serialNo = d.serialNo + 1
        }
      }
      // 新增一个箱子
      const container = new Container().fillDefaultValue().getData()
      Object.assign(container, this.defaultSeaEQ)
      container.eqSize = this.defaultEqSize
      container.eqType = this.defaultEqType
      container.jobUuid = this.job.uuid
      container.uuid = 'addContainer_' + getUuid()
      container.isDirty = true
      const u = new ContainerUsage().fillDefaultValue().getData()
      u.isDirty = true
      u.container = container
      u.jobUuid = this.job.uuid
      u.eqSize = this.defaultEqSize
      u.eqType = this.defaultEqType
      u.serialNo = serialNo
      u.containerUuid = container.uuid
      u.uuid = 'addContainerUsage_' + getUuid()
      container.containerUsage.push(u)
      this.job.containers.push(container)
      this.job.containerUsages.push(u)
      ContainerUtil.createTrucking(u, this.job)
      // 重新计算下 seaEQ
      this.refreshSeaEqForAdd(this.defaultEqSize, this.defaultEqType)
    },
    refreshSeaEqForAdd(size, type) {
      let have = false
      for (const eq of this.job.seaEQ) {
        if (eq.eqSize === size && eq.eqType === type) {
          eq.eqQuantity = eq.eqQuantity + 1
          have = true
          break
        }
      }
      if (!have) {
        const seaEq = new SeaEQ().fillDefaultValue().getData()
        seaEq.eqSize = size
        seaEq.eqType = type
        seaEq.eqQuantity = 1
        seaEq.jobUuid = this.job.uuid
        this.job.seaEQ.push(seaEq)
      }
    },
    remove() {
      if (!this.selectedData) {
        return
      }
      if (this.selectedData.uuid) {
        this.selectedData.deleted = true
        const index = this.rowData.findIndex(p => p.uuid === this.selectedData.uuid)
        if (index > -1) {
          this.rowData.splice(index, 1)
        }
        if (!this.job.delContainerUuids) {
          this.job.delContainerUuids = []
        }
        // 删除掉子表数据
        if (this.selectedData && this.selectedData.container) {
          const dc = this.selectedData.container
          const dIndex = this.job.containers.findIndex(p => p.uuid === dc.uuid)
          if (dIndex > -1) {
            this.job.delContainerUuids.push(dc.uuid)
            this.job.containers.splice(dIndex, 1)
          }
        }
      } else {
        const index = this.rowData.findIndex(p => p === this.selectedData)
        if (index > -1) {
          this.rowData.splice(index, 1)
        }
      }
      this.job.containerUsages = this.rowData
      // 刷新seaEq内容
      this.refreshSeaEqForDelete(this.selectedData.eqSize, this.selectedData.eqType)
      this.selectedData = null
      this.gridApi.clearFocusedCell() // 清除选中行
    },
    refreshSeaEqForDelete(size, type) {
      const seaEqList = []
      if (!this.job.delseaEQUuids) {
        this.job.delseaEQUuids = []
      }
      for (const seaEq of this.job.seaEQ) {
        if (seaEq.eqSize === size && seaEq.eqType === type) {
          if (seaEq.eqQuantity > 1) {
            seaEq.eqQuantity = seaEq.eqQuantity - 1
            seaEqList.push(seaEq)
          } else if (seaEq.eqQuantity === 1 && seaEq.uuid) {
            // 只有一个就删了
            this.job.delseaEQUuids.push(seaEq.uuid)
          }
        } else {
          seaEqList.push(seaEq)
        }
      }
      this.job.seaEQ = seaEqList
    },
    async onCellChange(params) {
      this.$emit('cellChange', params)
      const data = params.data
      // 箱型箱种输入验证
      let field = params.field
      if (params.colDef) {
        field = params.colDef.field
      }
      if (field === 'eqSize' || field === 'eqType' || field === 'containerNo') {
        // 同步更新下子表
        if (data.container) {
          if (field === 'eqSize') {
            data.container.eqSize = params.value
          }
          if (field === 'eqType') {
            data.container.eqType = params.value
          }
          if (field === 'containerNo') {
            data.container.containerNo = params.newValue
          }
        }
        if (this.job.containers && data.container) {
          for (const d of this.job.containers) {
            if (field === 'eqSize' && data.container.uuid === d.uuid) {
              d.eqSize = params.value
            }
            if (field === 'eqType' && data.container.uuid === d.uuid) {
              d.eqType = params.value
            }
            if (field === 'containerNo' && data.container.uuid === d.uuid) {
              d.containerNo = params.newValue
            }
          }
        }
        // 更新下seaEQ
        // 原eqSize eqType -1,新增或更新
        switch (field) {
          case 'eqSize':
            this.refreshSeaEqForUpdate(params.originalValue, data.eqType, data.eqSize, data.eqType)
            break
          case 'eqType':
            this.refreshSeaEqForUpdate(data.eqSize, params.originalValue, data.eqSize, data.eqType)
            break
        }
      }
    },
    refreshSeaEqForUpdate(oldSize, oldType, size, type) {
      // 清理变更前的seaEq
      this.refreshSeaEqForDelete(oldSize, oldType)
      // 新内容添加
      this.refreshSeaEqForAdd(size, type)
    },
    subClassLabelFunction(data, fieldName) {
      if (fieldName === 'eqSize') {
        const eqSize = data.container ? EQSize.getEQSizeByCode(data.container[fieldName]) : null
        return eqSize === null ? '' : eqSize.name
      } else {
        if (!data.container) return ''
        return data.container[fieldName] || ''
      }
    },
    containerNbrStyleFunction(params) {
      const usage = params.data
      if (usage.container) {
        if (usage && !ContainerNoHelper.checkContainerNo(params.value)) {
          return { color: '#F56C6C' }
        }
        if (DataPushCompareUtils.comparePushData(this.$t('SeaExport.ContainerNo'), params.value, usage.container.uuid)) {
          return { color: '#1F75D5' }
        }
      }
      return { color: '' }
      // this.containerPushDataStyleFunction(params, 'ContainerNo')
    },
    rowClicked(event) {
      if (event) {
        this.selectedData = event.data
      }
    },
    onUpCellFocused(event) {
      if (this.gridOptions.api && event.column) {
        this.gridOptions.api.forEachNode(node => {
          if (node.rowIndex === event.rowIndex) {
            node.setSelected(true)
            this.selectedData = node.data
          }
        })
      }
    }
  }
}
</script>

<style lang="scss">
.booking-sea-eq-data-grid {
  .ag-header-cell-text {
    color: #4283ff;
    font-weight: bold;
  }
}
</style>
